import { toast } from "react-hot-toast";
import { useOffers } from "../../store";
import {
  Button,
  CaretIcon,
  FilterIcon,
  Menu,
  useDisclosure,
} from "../../../../../shared/infrastructure";
import {
  CarrierSelect,
  CommoditySelect,
  DispatcherSelect,
  EquipmentTypeSelect,
  LocationMarketsSelect,
  RevenueCodeSelect,
} from "./search-field-selects";
import {
  CarrierT,
  CommodityT,
  DispatcherT,
  DistanceList,
  EquipmentTypeT,
  OfferCardSectionT,
  RadiusFromList,
  RegionList,
  Zip3MarketsT,
} from "../../../domain";
import { Drawer as MUIDrawer, TextField } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";

type OffersSidebarFiltersProps = {
  tab: OfferCardSectionT;
};

type TempOffersFilters = Partial<{
  approval_id: string;
  orderNumber: string;
  carrier: Array<CarrierT>;
  equipmentType: Array<EquipmentTypeT>;
  dispatcher: Array<DispatcherT>;
  commodity: Array<CommodityT>;
  revenueCode: string;
  origin: Array<Zip3MarketsT>;
  pick_up_date: string;
  destination: Array<Zip3MarketsT>;
  drop_off_date: string;
  region: string;
  distance_from: string;
  distance: number;
}>;

type SetTempFilterT = <
  N extends keyof TempOffersFilters,
  V extends TempOffersFilters[N]
>(
  name: N,
  value: V
) => void;

export const OffersSidebarFilters = ({
  tab,
}: OffersSidebarFiltersProps): React.ReactElement => {
  const { clearFilters, setTypeFilters, resetFilters, setResetFilters } =
    useOffers();
  const { isOpen, open, close } = useDisclosure();
  const [filtersApplied, setFiltersApplied] = useState({
    queue: false,
    history: false,
  });
  const [tempFilters, setTempFilters] = useState<{
    queue: TempOffersFilters;
    history: TempOffersFilters;
  }>({
    queue: {},
    history: {},
  });

  const { params } =
    useRoute<RouteProp<{ approvals: { approvalId: string } }, "approvals">>();
  const { setParams } = useNavigation();

  const apply = useCallback(
    (filters: TempOffersFilters) => {
      setTypeFilters(tab, {
        carrier: filters.carrier?.map((item) => item.carrier_id),
        equipment_type: filters.equipmentType?.map(
          (item) => item.equipment_code
        ),
        dispatcher: filters.dispatcher?.map((item) => item.dispatcher),
        commodity: filters.commodity?.map((item) => item.description),
        revenue_code_id: filters.revenueCode,
        /*
         * origin: filters.origin?.map(
         *   (item) => `${item.city}, ${item.state_id ?? item.state_code}`
         * ),
         */
        pick_up_date: filters.pick_up_date,
        /*
         * destination: filters.destination?.map(
         *   (item) => `${item.city}, ${item.state_id ?? item.state_code}`
         * ),
         */
        drop_off_date: filters.drop_off_date,
        region: filters.region,
        distance_from: filters.distance_from,
        distance: filters.distance,
        order_number: filters.orderNumber,
        approval_id: filters.approval_id,
        origin_market: filters.origin
          ?.map((item) => item.market)
          .filter((market) => !!market),
        destination_market: filters.destination
          ?.map((item) => item.market)
          .filter((market) => !!market),
      });
      setFiltersApplied((prev) => ({
        ...prev,
        [tab]: true,
      }));
    },
    [setTypeFilters, tab]
  );

  const clear = useCallback(() => {
    clearFilters(tab);
    setParams({ approvalId: "" });
    setTempFilters((prev) => ({
      ...prev,
      [tab]: {},
    }));
    setFiltersApplied((prev) => ({
      ...prev,
      [tab]: false,
    }));
  }, [clearFilters, tab, setParams]);

  const setTempFilter = useCallback<(tab: OfferCardSectionT) => SetTempFilterT>(
    (tab) => (name, value) => {
      setTempFilters((prev) => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          [name]: value,
        },
      }));
    },
    []
  );

  useEffect(() => {
    const { origin, destination, distance_from } = tempFilters[tab];
    if (
      origin === undefined &&
      (distance_from === "ORIGIN" || distance_from === "BOTH")
    ) {
      setTempFilter(tab)("distance_from", undefined);
      setTempFilter(tab)("distance", undefined);
    }
    if (
      destination === undefined &&
      (distance_from === "DESTINATION" || distance_from === "BOTH")
    ) {
      setTempFilter(tab)("distance_from", undefined);
      setTempFilter(tab)("distance", undefined);
    }
  }, [setTempFilter, tab, tempFilters]);

  const setFromValue = useCallback(
    (_: string, value: string) => {
      if (value === "ORIGIN") {
        if (tempFilters[tab].origin) setTempFilter(tab)("distance_from", value);
        else toast.error("Must select an origin first");
      }
      if (value === "DESTINATION") {
        if (tempFilters[tab].destination)
          setTempFilter(tab)("distance_from", value);
        else toast.error("Must select a destination first");
      }
      if (value === "BOTH") {
        if (tempFilters[tab].origin && tempFilters[tab].destination)
          setTempFilter(tab)("distance_from", value);
        else toast.error("Must select an origin and a destination first");
      }
    },
    [setTempFilter, tab, tempFilters]
  );

  const setRadiusValue = useCallback(
    (_: string, value: number) => {
      if (tempFilters[tab].distance_from) setTempFilter(tab)("distance", value);
      else
        toast.error(
          "Must select a location from, first before select a radius"
        );
    },
    [setTempFilter, tab, tempFilters]
  );

  // Watch if the filters are cleared so we can reset the temp filters
  useEffect(() => {
    if (resetFilters) {
      clear();
      setResetFilters(false);
    }
  }, [resetFilters, clear, setResetFilters]);

  useEffect(() => {
    if (params?.approvalId) {
      apply({
        approval_id: params.approvalId,
      });
    }
  }, [params?.approvalId, tab, apply]);

  return (
    <React.Fragment>
      <Button
        className={`ml-auto mr-5 w-auto border-0 border-transparent bg-transparent text-[14px]  font-[700] shadow-[0] md:ml-0 md:mr-0 ${
          filtersApplied[tab] ? "text-[#147EFB]" : ""
        }`}
        endIcon={<FilterIcon />}
        startIcon={
          filtersApplied[tab] ? (
            <span className="h-[10px] w-[10px] rounded-full bg-[#147EFB]" />
          ) : null
        }
        appearance="secondary"
        onClick={open}
      >
        Filters
      </Button>
      <MUIDrawer anchor="right" open={isOpen} onClose={close}>
        <section className="grid h-full w-[238px] grid-rows-[1fr_auto]">
          <section className="h-full overflow-y-auto overflow-x-hidden">
            <section className="grid gap-[24px] p-[20px]">
              <section className="grid">
                <h3 className="mb-[16px] text-[12px] font-[700]">Approvals</h3>
                <input
                  name="order_id"
                  placeholder="Order ID"
                  type="text"
                  value={tempFilters[tab].orderNumber ?? ""}
                  onChange={(e) => {
                    setTempFilter(tab)("orderNumber", e.target.value);
                  }}
                  className="border-b-solid mb-[16px] border-0 border-b-[1px] border-b-[#CFCFCF] px-[16px] py-[5px] text-[14px] outline-0 placeholder:text-[#CFCFCF]"
                />
                <CarrierSelect
                  name="carrier"
                  keyProp="carrier_id"
                  multiselect
                  selectedValue={tempFilters[tab].carrier}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      setTempFilter(tab)("carrier", value);
                    }
                  }}
                  placeholder="Carrier"
                />
                <EquipmentTypeSelect
                  name="equipment_type"
                  keyProp="equipment_code"
                  multiselect
                  selectedValue={tempFilters[tab].equipmentType}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      setTempFilter(tab)("equipmentType", value);
                    }
                  }}
                  placeholder="Equipment Type"
                />
                <DispatcherSelect
                  name="dispatcher"
                  keyProp="dispatcher"
                  multiselect
                  selectedValue={tempFilters[tab].dispatcher}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      setTempFilter(tab)("dispatcher", value);
                    }
                  }}
                  placeholder="Dispatcher"
                />
                <CommoditySelect
                  name="commodity"
                  keyProp="description"
                  multiselect
                  selectedValue={tempFilters[tab].commodity}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      setTempFilter(tab)("commodity", value);
                    }
                  }}
                  placeholder="Commodity"
                />
                <RevenueCodeSelect
                  name="revenue_code_id"
                  keyProp="id"
                  selectedValue={tempFilters[tab].revenueCode}
                  onChangeValue={(value) => {
                    if (!Array.isArray(value)) {
                      setTempFilter(tab)("revenueCode", value?.id);
                    }
                  }}
                  placeholder="Revenue Code"
                />
              </section>
              <section className="grid">
                <h3 className="mb-[16px] text-[12px] font-[700]">Shipper</h3>
                <LocationMarketsSelect
                  name="origin"
                  keyProp="market"
                  multiselect
                  selectedValue={tempFilters[tab].origin}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      if (value.length === 0)
                        setTempFilter(tab)("origin", undefined);
                      else setTempFilter(tab)("origin", value);
                    }
                  }}
                  placeholder="Origin"
                />
                <TextField
                  id="pick_up_date"
                  type="date"
                  className="mb-[16px] text-[14px] outline-0"
                  value={tempFilters[tab].pick_up_date ?? ""}
                  placeholder="Pick Up Date"
                  inputProps={{
                    className: "px-[16px] py-[5px] placeholder:text-[#CFCFCF]",
                  }}
                  onChange={(e) =>
                    setTempFilter(tab)("pick_up_date", e.target.value)
                  }
                />
              </section>
              <section className="grid">
                <h3 className="mb-[16px] text-[12px] font-[700]">Receiver</h3>
                <LocationMarketsSelect
                  name="destination"
                  keyProp="market"
                  multiselect
                  selectedValue={tempFilters[tab].destination}
                  onChangeValue={(value) => {
                    if (Array.isArray(value)) {
                      if (value.length === 0)
                        setTempFilter(tab)("destination", undefined);
                      else setTempFilter(tab)("destination", value);
                    }
                  }}
                  placeholder="Destination"
                />
                <TextField
                  id="drop_off_date"
                  type="date"
                  className="mb-[16px] border-b-[#CFCFCF] text-[14px] outline-0"
                  value={tempFilters[tab].drop_off_date ?? ""}
                  placeholder="Pick Up Date"
                  inputProps={{
                    className: "px-[16px] py-[5px] placeholder:text-[#CFCFCF]",
                  }}
                  onChange={(e) =>
                    setTempFilter(tab)("drop_off_date", e.target.value)
                  }
                />
              </section>
              <section className="grid">
                <h3 className="mb-[16px] text-[12px] font-[700]">Region</h3>
                <Menu
                  name="region"
                  items={RegionList}
                  defaultValue={""}
                  selectedValue={tempFilters[tab].region}
                  renderItem={({ label }) => (
                    <section className="text-[12px] capitalize">
                      {label}
                    </section>
                  )}
                  setValue={(_, value) => setTempFilter(tab)("region", value)}
                >
                  {({ value, label }) => (
                    <Button
                      className="mb-[16px] h-auto w-full rounded-[0] border-0 border-b-[1px] border-transparent border-b-[#D3D3D3] bg-[#FFFFFF] py-[4px] px-[14px] text-[14px] capitalize hover:border-b-[#838383] hover:bg-[#FFFFFF]"
                      endIcon={
                        <CaretIcon width={16} height={16} fill="#838383" />
                      }
                      appearance="secondary"
                    >
                      <span className="w-full text-left">
                        {value === "" ? (
                          <span className="text-[#C8C8C8]">Region</span>
                        ) : (
                          label
                        )}
                      </span>
                    </Button>
                  )}
                </Menu>
              </section>
              <section className="grid">
                <section className="grid grid-flow-col items-center justify-start gap-[15px]">
                  <h3 className="text-[12px] font-[700]">Radius</h3>
                  <Menu
                    name="distance_from"
                    items={RadiusFromList}
                    defaultValue={""}
                    selectedValue={tempFilters[tab].distance_from}
                    renderItem={({ label }) => (
                      <section className="text-[12px] capitalize">
                        {label}
                      </section>
                    )}
                    setValue={setFromValue}
                  >
                    {({ value, label }) => (
                      <Button
                        className="h-auto w-full rounded-[0] border-b-0 border-transparent bg-[#FFFFFF] py-[4px] px-[10px] text-[14px] capitalize"
                        endIcon={
                          <CaretIcon width={16} height={16} fill="#838383" />
                        }
                        appearance="secondary"
                      >
                        <span className="w-full text-left">
                          {value === "" ? (
                            <span className="text-[#C8C8C8]">From</span>
                          ) : (
                            label
                          )}
                        </span>
                      </Button>
                    )}
                  </Menu>
                </section>
                <Menu
                  name="distance"
                  items={DistanceList}
                  defaultValue={0}
                  selectedValue={tempFilters[tab].distance}
                  renderItem={({ value }) => (
                    <section className="text-[12px] capitalize">
                      {value} mi
                    </section>
                  )}
                  setValue={setRadiusValue}
                >
                  {({ value }) => (
                    <Button
                      className="mb-[16px] h-auto w-full rounded-[0] border-0 border-b-[1px] border-transparent border-b-[#D3D3D3] bg-[#FFFFFF] py-[4px] px-[14px] text-[14px] capitalize hover:border-b-[#838383] hover:bg-[#FFFFFF]"
                      endIcon={
                        <CaretIcon width={16} height={16} fill="#838383" />
                      }
                      appearance="secondary"
                    >
                      <span className="w-full text-left">
                        {value === 0 ? (
                          <span className="text-[#C8C8C8]">Distance</span>
                        ) : (
                          `${value} mi`
                        )}
                      </span>
                    </Button>
                  )}
                </Menu>
              </section>
            </section>
          </section>
          <section className="grid grid-cols-[1fr_1fr] gap-[20px] p-[20px]">
            <Button
              className="h-auto min-h-0 w-auto min-w-0 px-[16px] py-[6px] text-[12px]"
              appearance="secondary"
              onClick={clear}
            >
              Clear
            </Button>
            <Button
              className="h-auto min-h-0 w-auto min-w-0 px-[16px] py-[6px] text-[12px]"
              onClick={() => apply(tempFilters[tab])}
            >
              Search
            </Button>
          </section>
        </section>
      </MUIDrawer>
    </React.Fragment>
  );
};
